// Global SmartForms — Supabase service layer
// All DB types and helper functions for the app

export type DocumentStatus = 'Published' | 'Draft' | 'Hidden' | 'Archived';

export interface Category {
  id: string;
  name: string;
  slug: string;
  description: string;
  icon: string;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string | null;
  updated_by?: string | null;
  // computed
  document_count?: number;
}

export interface Document {
  id: string;
  title: string;
  slug: string;
  description: string;
  keywords: string[];
  category_id: string;
  status: DocumentStatus;
  storage_path: string;
  original_filename: string;
  mime_type: string;
  file_size: number;
  version_number: number;
  created_at: string;
  updated_at: string;
  published_at: string | null;
  created_by?: string | null;
  updated_by?: string | null;
  // joined
  category?: Category;
}

export interface AuditLog {
  id: string;
  actor_user_id: string | null;
  actor_name: string;
  action_type: string;
  entity_type: string;
  entity_id: string;
  entity_name: string;
  old_values: Record<string, any>;
  new_values: Record<string, any>;
  metadata: Record<string, any>;
  created_at: string;
}

export interface Profile {
  id: string;
  full_name: string;
  role: 'admin' | 'super_admin';
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const day = d.getDate().toString().padStart(2, '0');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${day} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

export function formatFileSize(bytes: number): string {
  if (!bytes || bytes === 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function getStatusClass(status: DocumentStatus): string {
  switch (status) {
    case 'Published': return 'status-published';
    case 'Draft': return 'status-draft';
    case 'Hidden': return 'status-hidden';
    case 'Archived': return 'status-archived';
    default: return 'status-hidden';
  }
}

export function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 80);
}
